// src/pages/billing/TransactionListPage.tsx

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useBillingTransaction, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatMoney(money: any): string {
  if (!money) return '--';
  const amount = (money.amount ?? 0) / Math.pow(10, money.decimal_places ?? 2);
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(dateStr); }
}

function txStatusVariant(status: string): 'success' | 'warning' | 'error' | 'primary' | 'secondary' {
  switch (status) {
    case 'TRANSACTION_STATUS_COMPLETED': return 'success';
    case 'TRANSACTION_STATUS_PENDING': case 'TRANSACTION_STATUS_PROCESSING': return 'warning';
    case 'TRANSACTION_STATUS_FAILED': case 'TRANSACTION_STATUS_DISPUTED': return 'error';
    case 'TRANSACTION_STATUS_REFUNDED': case 'TRANSACTION_STATUS_PARTIALLY_REFUNDED': return 'primary';
    case 'TRANSACTION_STATUS_CANCELLED': return 'secondary';
    default: return 'secondary';
  }
}

function enumLabel(value: string, prefix: string): string {
  return (value || '').replace(prefix, '').replace(/_/g, ' ');
}

export const TransactionListPage = observer(() => {
  const store = useBillingTransaction();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');

  useEffect(() => {
    store.ListTransactions({
      pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
      sort: [], date_range: {} as any, filters: {} as any,
    });
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">Transactions</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">Transactions</h1>
        </div>
        <Link to="/billing/transactions/new"><Button>Create Transaction</Button></Link>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} fullWidth={false} className="flex-1" />
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={6} message="No transactions found" />
            ) : (
              store.items.map((item: any) => (
                <TableRow key={item.entity?.id || item.id || Math.random()}>
                  <TableCell>{formatDate(item.created_at)}</TableCell>
                  <TableCell>{enumLabel(item.type, 'TRANSACTION_TYPE_')}</TableCell>
                  <TableCell>{String(item.description ?? '--')}</TableCell>
                  <TableCell><Badge variant={txStatusVariant(item.status)} size="sm">{enumLabel(item.status, 'TRANSACTION_STATUS_')}</Badge></TableCell>
                  <TableCell>{formatMoney(item.amounts?.net)}</TableCell>
                  <TableCell className="text-right">
                    <Link to={`/billing/transactions/${item.entity?.id || item.id}`}><Button variant="ghost" size="sm">View</Button></Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default TransactionListPage;
